import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Grid, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import dayjs from 'dayjs';

import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import GlobalTable from '../../../../components/CustomTableWithPagination/GlobalTable';
import FromAndToDatePicker from '../../../../components/mui/FromAndToDatePicker';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import StatCard from './StatCard';
import { CRO_ASSESSMENT_ANALYTICS_TABLE_HEAD } from '../../../../components/datagrid/myworkspace/cro/CroAssessmentAnalyticsTableHead';
import { getCroAssessmentAnalytics } from '../../../../redux/features/croSlice';
import AssessmentReportContent from './AssessmentReportContent';

/**
 * Assessment analytics for the logged in CRO — students assessed in the selected
 * date range and what they actually enrolled in. The CRO is taken from the auth
 * token on the backend, so there is deliberately no CRO dropdown here.
 */
export default function CroAssessmentAnalytics() {
  const dispatch = useDispatch();
  const { croAssessmentAnalytics, loading } = useSelector((state) => state.cro);

  const [fromDate, setFromDate] = useState(dayjs().startOf('year').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(dayjs().endOf('year').format('YYYY-MM-DD'));

  // 'all' | 'gap' -- 'gap' keeps only students with a subject they were assessed
  // in but never enrolled in.
  const [filter, setFilter] = useState('all');

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 50,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: [],
    hiddenColumns: [],
  });

  const dispatchParams = useMemo(
    () => ({
      page: tableState?.page,
      limit: tableState?.pageSize,
      search: tableState?.search,
      fromDate,
      toDate,
      filter,
    }),
    [tableState?.page, tableState?.pageSize, tableState?.search, fromDate, toDate, filter],
  );

  useEffect(() => {
    dispatch(getCroAssessmentAnalytics(dispatchParams));
  }, [dispatchParams, dispatch]);

  // The rubric is only fetched once a CRO actually opens a student.
  const [reportRow, setReportRow] = useState(null);

  const handleButtonActionClick = useCallback((actionKey, row) => {
    if (actionKey === 'Assessment Report' && row) setReportRow(row);
  }, []);

  const stats = croAssessmentAnalytics?.stats ?? {};

  const statCards = useMemo(
    () => [
      {
        title: 'Students Assessed',
        total: stats?.total_students_assessed ?? 0,
        icon: 'mdi:account-school',
        color: 'primary',
      },
      {
        title: 'Assessments Held',
        total: stats?.total_assessments ?? 0,
        icon: 'mdi:clipboard-check-outline',
        color: 'info',
      },
      {
        title: 'Fully Enrolled',
        total: stats?.fully_enrolled ?? 0,
        icon: 'mdi:account-check',
        color: 'success',
      },
      {
        title: 'Assessed, Not Enrolled',
        total: stats?.not_enrolled ?? 0,
        icon: 'mdi:account-alert',
        color: 'error',
      },
      // Without this card the two above read as good news, when in reality most
      // students are on batch courses whose subjects were never recorded.
      {
        title: 'Subjects Not Tracked',
        total: stats?.subjects_not_tracked ?? 0,
        icon: 'mdi:help-circle-outline',
        color: 'warning',
      },
    ],
    [
      stats?.total_students_assessed,
      stats?.total_assessments,
      stats?.fully_enrolled,
      stats?.not_enrolled,
      stats?.subjects_not_tracked,
    ],
  );

  const reportTitle = reportRow
    ? `Assessment Report — ${reportRow.name} (${reportRow.admn_no})`
    : 'Assessment Report';

  return (
    <GlobalWrapper title={'CRO Assessment Analytics'}>
      <Stack gap={3}>
        {/* five across on desktop, so the cards stay a summary strip rather
            than filling the screen above the table */}
        <Grid container spacing={1.5}>
          {statCards.map((card) => (
            <Grid item xs={6} sm={4} md={2.4} key={card.title}>
              <StatCard
                title={card.title}
                total={card.total}
                icon={card.icon}
                color={card.color}
              />
            </Grid>
          ))}
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} alignItems="center">
          <FromAndToDatePicker
            fromLabel="From Date"
            toLabel="To Date"
            fromDate={fromDate}
            toDate={toDate}
            setFromDate={setFromDate}
            setToDate={setToDate}
            /**
             * Shrunk to match the 40px "Show" select beside it. Done through the
             * existing containerStyle prop rather than by editing
             * FromAndToDatePicker, which many other pages render at full size.
             * These descendant selectors outrank the inline height/fontSize
             * DatePickerCustom sets on its own InputProps.
             */
            containerStyle={{
              '& .MuiInputBase-root': { height: 40 },
              '& .MuiInputBase-input': { fontSize: 14, py: 0 },
              '& .MuiFormLabel-root': { fontSize: 14 },
              // keep the shrunk label sitting on the notch, not floating high
              '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                transform: 'translate(14px, 10px) scale(1)',
              },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="cro-assessment-filter-label">Show</InputLabel>
            <Select
              labelId="cro-assessment-filter-label"
              label="Show"
              value={filter}
              onChange={(event) => {
                setFilter(event.target.value);
                // the filtered set is shorter, so an old page number can land
                // past the end of the results
                setTableState((prev) => ({ ...prev, page: 1 }));
              }}
            >
              <MenuItem value="all">All assessed students</MenuItem>
              <MenuItem value="gap">Assessed but not enrolled only</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <GlobalTable
          rowUniqueId="sl_no"
          headers={CRO_ASSESSMENT_ANALYTICS_TABLE_HEAD}
          rows={croAssessmentAnalytics?.data}
          meta={croAssessmentAnalytics?.meta}
          loading={loading}
          tableState={tableState}
          setTableState={setTableState}
          onButtonActionClick={handleButtonActionClick}
          exportParams={{ ...dispatchParams, page: 1, limit: 'no' }}
          exportApi={getCroAssessmentAnalytics}
        />
      </Stack>

      <GlobalDialog
        title={reportTitle}
        open={Boolean(reportRow)}
        setOpen={(val) => {
          if (!val) setReportRow(null);
        }}
        closeButton={true}
        maxWidth="lg"
        fullWidth={true}
        content={
          reportRow ? (
            <AssessmentReportContent admnNo={reportRow.admn_no} studentName={reportRow.name} />
          ) : null
        }
      />
    </GlobalWrapper>
  );
}
